/** The shapes `FilterBar` takes and emits, and the two helpers a screen needs beside it. */

export interface FilterOption {
  value: string;
  label: string;
  /** A count the server gave for this option. Printed beside it; never computed here. */
  count?: number;
}

export interface FilterDef {
  id: string;
  label: string;
  options: readonly FilterOption[];
  /** `single` picks one (and has an "All" entry); `multi` toggles any number. Default `multi`. */
  mode?: "single" | "multi";
  /** The single-select's "no filter" entry. Default `All`. */
  allLabel?: string;
}

export interface FilterState {
  search: string;
  /** Filter id to selected option values. A missing id or an empty list is "no filter". */
  values: Readonly<Record<string, readonly string[]>>;
}

export const EMPTY_FILTERS: FilterState = { search: "", values: {} };

/** How many filters are narrowing the list (search counts as one). */
export function activeFilterCount(state: FilterState): number {
  const chips = Object.values(state.values).filter((v) => v.length > 0).length;
  return chips + (state.search.trim() ? 1 : 0);
}

/** The option lists with a count beside each option: how many of `rows` carry that value.
 *
 * A count of rows the page holds - not a figure, and not the server's. The page decides the universe
 * (all the rows loaded, ignoring the other filters, so an option's count is what choosing it would add)
 * and what a row's value for a filter is (`valuesOf` returns one value or several). A definition with
 * a count already on its options is left alone: the server's own count wins over ours.
 */
export function withCounts<T>(
  defs: readonly FilterDef[],
  rows: readonly T[],
  valuesOf: (row: T, filterId: string) => string | readonly string[] | null | undefined,
): FilterDef[] {
  return defs.map((def) => {
    if (def.options.some((option) => option.count !== undefined)) return def;
    const counts = new Map<string, number>();
    for (const row of rows) {
      const got = valuesOf(row, def.id);
      const list = got === null || got === undefined ? [] : typeof got === "string" ? [got] : got;
      for (const value of new Set(list)) counts.set(value, (counts.get(value) ?? 0) + 1);
    }
    return { ...def, options: def.options.map((option) => ({ ...option, count: counts.get(option.value) ?? 0 })) };
  });
}
